import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Live camera preview.
 *
 * The video element lives inside a tab panel that Radix unmounts when hidden,
 * so the stream must be (re)attached whenever the element appears, and the
 * capture must restart after the panel is remounted.
 */
export function useCameraPreview(deviceId?: string, enabled = true) {
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isActive, setIsActive] = useState(false);

  const attach = useCallback((el: HTMLVideoElement | null) => {
    videoElRef.current = el;
    if (el && streamRef.current) {
      el.srcObject = streamRef.current;
      void el.play().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Do not touch the camera until the caller opts in (e.g. the Devices tab
    // is open) — otherwise merely opening settings triggers a permission prompt.
    if (!enabled) return;

    const apply = (next: MediaStream) => {
      if (cancelled) {
        next.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = next;
      setStream(next);
      setIsActive(true);
      if (videoElRef.current) {
        videoElRef.current.srcObject = next;
        void videoElRef.current.play().catch(() => undefined);
      }
    };

    const start = async () => {
      try {
        apply(
          await navigator.mediaDevices.getUserMedia({
            video: deviceId ? { deviceId: { exact: deviceId } } : true,
          }),
        );
      } catch {
        if (cancelled || !deviceId) {
          if (!cancelled) setIsActive(false);
          return;
        }
        try {
          apply(await navigator.mediaDevices.getUserMedia({ video: true }));
        } catch {
          if (!cancelled) setIsActive(false);
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setStream(null);
      setIsActive(false);
      if (videoElRef.current) videoElRef.current.srcObject = null;
    };
  }, [deviceId, enabled]);

  // Re-attach when the element mounts after the stream is already live.
  useEffect(() => {
    if (videoElRef.current && stream) {
      videoElRef.current.srcObject = stream;
      void videoElRef.current.play().catch(() => undefined);
    }
  }, [stream]);

  return { videoRef: attach, isActive };
}
